import { type ReactNode, useCallback, useMemo, useRef, useState } from 'react';

import { Icon, type IconName } from '@/components/ui/Icon';
import { cn } from '@/utils/cn';

import styles from './Toast.module.css';
import { type ToastApi, ToastContext, type ToastTone } from './toast-context';

interface ToastItem {
  id: number;
  tone: ToastTone;
  message: string;
}

const DURATION_MS: Record<ToastTone, number> = { success: 3500, info: 4500, error: 7000 };
const ICONS: Record<ToastTone, IconName> = { success: 'check', info: 'info', error: 'alert' };

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback(
    (tone: ToastTone, message: string) => {
      const id = nextId.current++;
      setToasts((current) => [...current.slice(-2), { id, tone, message }]);
      window.setTimeout(() => {
        dismiss(id);
      }, DURATION_MS[tone]);
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      success: (message) => {
        show('success', message);
      },
      error: (message) => {
        show('error', message);
      },
      info: (message) => {
        show('info', message);
      },
    }),
    [show],
  );

  return (
    <ToastContext value={api}>
      {children}
      <div className={styles.region} role="status" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className={cn(styles.toast, styles[toast.tone])}>
            <Icon name={ICONS[toast.tone]} size={20} className={styles.icon} />
            <p className={styles.message}>{toast.message}</p>
            <button
              type="button"
              className={styles.close}
              aria-label="Dismiss"
              onClick={() => {
                dismiss(toast.id);
              }}
            >
              <Icon name="close" size={16} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext>
  );
}
